/**
 * Filas agrupadas del carrito THCC (cantidad, +/-, quitar).
 *
 * Los descuentos automaticos "compra X, lleva Y" de Shopify parten una misma variante en varias lineas del carrito
 * (las unidades que califican llevan la asignacion de descuento; el resto queda en otra linea con otra clave) y las
 * vuelven a partir cada vez que se intenta unirlas. Por eso snippets/cart-products.liquid solo AGRUPA al pintar: una
 * fila por (variante + propiedades + plan de venta) con la cantidad y el total sumados, y marca esa fila con
 * `data-thcc-grupo='[{"key":"...","qty":4},{"key":"...","qty":3}]'` (todas las lineas del grupo, con su cantidad).
 *
 * Este modulo hace que los controles de esas filas actuen sobre el GRUPO y no sobre la primera linea:
 *   - Cambio de cantidad (+, -, o numero escrito): Horizon emite QuantitySelectorUpdateEvent
 *     (`quantity-selector:update`, burbujea) con la linea de la primera fila. Se intercepta en la fase de captura de
 *     `window` (antes que el oyente con debounce de CartItemsComponent, que escucha en `document`) y se corta con
 *     stopImmediatePropagation.
 *   - Quitar: el `on:click="/onLineItemRemove/N"` de `.cart-items__remove` lo despacha component.js con un oyente de
 *     captura en `document`; aqui se intercepta el mismo click en `window` (captura), que corre antes.
 * Solo filas con `data-thcc-grupo` valido y mas de una linea. Las filas de una sola linea conservan el
 * comportamiento nativo de Horizon sin tocar.
 *
 * Escritura: UN POST a /cart/update.js con `updates` {clave: cantidad} para el grupo entero, pidiendo en la misma
 * llamada las secciones que repinta el carrito. Nuevo total T:
 *   - T >= suma de las demas lineas: la primera queda en T - suma(demas) y las demas no se tocan.
 *   - T <  suma de las demas lineas: la primera queda en 0 y se recortan las demas desde la ultima hacia atras
 *     hasta que el grupo suma T.
 *   - T = 0 (quitar): todas las claves en 0.
 * Despues se despacha un StandardEvents.cartLinesUpdate con esas secciones (mismo patron que thcc-cart-regalo.js) para
 * que Horizon repinte cajon / pagina y los contadores de la cabecera. Ese evento tambien dispara la reconciliacion del
 * regalo automatico. La escritura se hace bajo el mismo Web Lock del regalo para no cruzarse con una pasada suya.
 *
 * Sin el modulo de eventos no se intercepta nada (las filas agrupadas quedan con el comportamiento nativo).
 */

const FILA = 'tr[data-thcc-grupo]';
const EVENTO_CANTIDAD = 'quantity-selector:update';
const CANDADO = 'thcc-cart-regalo';
const ESPERA_MS = 300;
const FIN_MS = 350;

/** @type {any} */
let eventos = null;
/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const esperas = new Map();
/** @type {Promise<void>} */
let cola = Promise.resolve();

/** @param {string} ruta */
function url(ruta) {
  // @ts-ignore
  const raiz = typeof window.Shopify?.routes?.root === 'string' ? window.Shopify.routes.root : '/';
  return `${raiz.replace(/\/$/, '')}${ruta}`;
}

/** @returns {string[]} */
function seccionesDelCarrito() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * Lee las lineas del grupo de una fila. Devuelve null si la fila no es un grupo valido de varias lineas con claves
 * distintas (con claves repetidas un mapa de `updates` no puede repartir las cantidades).
 * @param {Element | null | undefined} fila
 * @returns {{ key: string, qty: number }[] | null}
 */
function leerGrupo(fila) {
  if (!(fila instanceof HTMLElement)) return null;
  try {
    const datos = JSON.parse(fila.dataset.thccGrupo || '[]');
    if (!Array.isArray(datos) || datos.length < 2) return null;
    const grupo = datos.map((/** @type {any} */ d) => ({
      key: String(d?.key ?? ''),
      qty: Math.max(0, Math.floor(Number(d?.qty) || 0)),
    }));
    if (grupo.some((g) => !g.key)) return null;
    if (new Set(grupo.map((g) => g.key)).size !== grupo.length) return null;
    return grupo;
  } catch (_) {
    return null;
  }
}

/**
 * Reparte el nuevo total del grupo entre sus lineas y devuelve solo las claves que cambian.
 * @param {{ key: string, qty: number }[]} grupo
 * @param {number} total
 * @returns {Record<string, number>}
 */
function calcularCambios(grupo, total) {
  const [primera, ...otras] = grupo;
  const sumaOtras = otras.reduce((s, l) => s + l.qty, 0);
  /** @type {Record<string, number>} */
  const nuevas = {};

  if (total >= sumaOtras) {
    nuevas[primera.key] = total - sumaOtras;
  } else {
    nuevas[primera.key] = 0;
    let sobrante = sumaOtras - total;
    for (let i = otras.length - 1; i >= 0; i -= 1) {
      const quita = Math.min(otras[i].qty, sobrante);
      nuevas[otras[i].key] = otras[i].qty - quita;
      sobrante -= quita;
    }
  }

  /** @type {Record<string, number>} */
  const cambios = {};
  for (const linea of grupo) {
    if (linea.key in nuevas && nuevas[linea.key] !== linea.qty) cambios[linea.key] = nuevas[linea.key];
  }
  return cambios;
}

/**
 * @param {string} ruta
 * @param {any} cuerpo
 */
async function escribir(ruta, cuerpo) {
  const respuesta = await fetch(url(ruta), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  /** @type {any} */
  let datos = null;
  try {
    datos = await respuesta.json();
  } catch (_) {
    datos = null;
  }
  if (!respuesta.ok || datos?.status || datos?.errors) {
    throw new Error(datos?.description || datos?.message || datos?.errors || `HTTP ${respuesta.status}`);
  }
  return datos;
}

/**
 * Avisa a Horizon (y a los contadores) del cambio. Se despacha en `document`, asi que todos los
 * cart-items-component (cajon y pagina) repintan su seccion con el HTML de la respuesta.
 * @param {any} carrito - Respuesta de /cart/update.js
 * @param {string} claveBase - Clave de la primera linea del grupo
 * @param {number} total - Cantidad total pedida para el grupo
 */
function anunciar(carrito, claveBase, total) {
  const { CartLinesUpdateEvent } = eventos;
  const { promise, resolve } = CartLinesUpdateEvent.createPromise();
  resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
    detail: {
      sections: carrito.sections,
      items: carrito.items,
      itemCount: carrito.item_count,
      source: 'thcc-cart-grupos',
      didError: false,
    },
  });
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: total > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: claveBase, quantity: total }],
      promise,
    })
  );
}

/**
 * @param {HTMLElement[]} filas
 * @param {string} mensaje
 * @param {number} suma - Cantidad que el grupo tenia antes del intento
 */
function mostrarError(filas, mensaje, suma) {
  for (const fila of filas) {
    const entrada = fila.querySelector('input[type="number"]');
    if (entrada instanceof HTMLInputElement) entrada.value = String(suma);
    const selector = fila.querySelector('cart-quantity-selector-component');
    /** @type {any} */ (selector)?.updateButtonStates?.();

    const contenedor = fila.querySelector('.cart-items__error');
    const texto = fila.querySelector('.cart-item__error-text');
    if (contenedor instanceof HTMLElement && texto instanceof HTMLElement) {
      texto.textContent = mensaje;
      contenedor.classList.remove('hidden');
    }
  }
  try {
    const { CartErrorEvent } = eventos;
    const origen = filas[0]?.closest('cart-items-component') ?? document;
    origen.dispatchEvent(new CartErrorEvent({ error: mensaje || 'Cart update failed', code: 'INVALID' }));
  } catch (_) {
    // El mensaje en la fila ya avisa al cliente.
  }
}

/**
 * @param {string} claveBase - Clave de la primera linea del grupo (identifica la fila)
 * @param {number} total
 * @param {{ key: string, qty: number }[]} grupoCapturado
 */
async function aplicar(claveBase, total, grupoCapturado) {
  const filas = Array.from(document.querySelectorAll(FILA)).filter(
    (f) => f instanceof HTMLElement && f.dataset.key === claveBase
  );
  const grupo = leerGrupo(filas[0]) ?? grupoCapturado;
  const suma = grupo.reduce((s, l) => s + l.qty, 0);
  const cambios = calcularCambios(grupo, total);

  if (Object.keys(cambios).length === 0) {
    // Nada que cambiar en el carrito: solo se deja el campo con la cantidad real.
    for (const fila of filas) {
      const entrada = fila.querySelector('input[type="number"]');
      if (entrada instanceof HTMLInputElement) entrada.value = String(suma);
    }
    return;
  }

  const componentes = new Set(filas.map((f) => f.closest('cart-items-component')).filter(Boolean));
  for (const componente of componentes) componente?.classList.add('cart-items-disabled');
  for (const fila of filas) {
    /** @type {any} */ (fila.querySelector('text-component'))?.shimmer?.();
  }

  try {
    const secciones = seccionesDelCarrito();
    const extra = secciones.length ? { sections: secciones.join(','), sections_url: window.location.pathname } : {};
    const carrito = await escribir('/cart/update.js', { updates: cambios, ...extra });
    try {
      anunciar(carrito, grupo[0].key, total);
    } catch (_) {
      // El carrito ya cambio en el servidor pero no se pudo avisar a Horizon: se recarga para mostrarlo.
      window.location.reload();
    }
  } catch (error) {
    console.warn('[thcc-cart-grupos] no se pudo actualizar el grupo', error);
    mostrarError(filas, /** @type {any} */ (error)?.message || '', suma);
  } finally {
    // Horizon repinta la seccion en cuanto recibe el evento: se da un instante antes de volver a habilitar.
    await new Promise((resolver) => setTimeout(resolver, FIN_MS));
    for (const componente of componentes) componente?.classList.remove('cart-items-disabled');
    for (const componente of componentes) {
      componente?.querySelectorAll('[shimmer]').forEach((el) => el.removeAttribute('shimmer'));
    }
  }
}

/** @param {() => Promise<void>} tarea */
function conCandado(tarea) {
  const locks = /** @type {any} */ (navigator).locks;
  if (locks && typeof locks.request === 'function') return locks.request(CANDADO, tarea);
  return tarea();
}

/**
 * Encola una escritura: una a la vez, en el orden en que el cliente las pidio.
 * @param {string} claveBase
 * @param {number} total
 * @param {{ key: string, qty: number }[]} grupo
 */
function encolar(claveBase, total, grupo) {
  cola = cola
    .then(() => conCandado(() => aplicar(claveBase, total, grupo)))
    .catch((error) => console.warn('[thcc-cart-grupos]', error));
}

/**
 * Programa el cambio con debounce por grupo (el ultimo valor gana), como el oyente nativo de Horizon.
 * @param {{ key: string, qty: number }[]} grupo
 * @param {number} total
 * @param {boolean} [inmediato]
 */
function programar(grupo, total, inmediato = false) {
  const claveBase = grupo[0].key;
  const previa = esperas.get(claveBase);
  if (previa) clearTimeout(previa);
  esperas.set(
    claveBase,
    setTimeout(
      () => {
        esperas.delete(claveBase);
        encolar(claveBase, total, grupo);
      },
      inmediato ? 0 : ESPERA_MS
    )
  );
}

/** @param {any} evento */
function alCambiarCantidad(evento) {
  const objetivo = evento.target;
  const fila = objetivo instanceof Element ? objetivo.closest(FILA) : null;
  const grupo = leerGrupo(fila);
  if (!grupo) return;

  evento.stopImmediatePropagation();
  const cantidad = Number(evento.detail?.quantity);
  if (!Number.isFinite(cantidad)) return;
  programar(grupo, Math.max(0, Math.floor(cantidad)));
}

/** @param {Event} evento */
function alHacerClick(evento) {
  const objetivo = evento.target;
  if (!(objetivo instanceof Element)) return;
  const boton = objetivo.closest('.cart-items__remove');
  if (!boton) return;
  const grupo = leerGrupo(boton.closest(FILA));
  if (!grupo) return;

  evento.preventDefault();
  evento.stopImmediatePropagation();
  programar(grupo, 0, true);
}

async function iniciar() {
  // @ts-ignore
  if (window.__thccCartGrupos) return;
  // @ts-ignore
  window.__thccCartGrupos = true;

  try {
    eventos = await import('@shopify/events');
  } catch (_) {
    // Sin el modulo de eventos no hay repintado coordinado: las filas agrupadas conservan el comportamiento nativo.
    return;
  }

  // `window` en captura corre antes que los oyentes de `document` (component.js y CartItemsComponent).
  window.addEventListener(EVENTO_CANTIDAD, alCambiarCantidad, { capture: true });
  window.addEventListener('click', alHacerClick, { capture: true });
}

iniciar();
